import { getConnectedEdges, Node, Edge } from "@xyflow/react";
import { useCallback } from "react";
import { useAtom, useSetAtom } from "jotai";
import {
  nodesAtom,
  edgesAtom,
  takeSnapshotSetter,
  MARKER,
  sidebarOpenNodeIdAtom,
  saveCanvasSetter,
} from "@zapier/canvas-state";

import { uuid } from "../utils/uuid";
import getFarthestNonDeletedIncomers from "../utils/getFarthestNonDeletedIncomers";
import getFarthestNonDeletedOutgoers from "../utils/getFarthestNonDeletedOutgoers";
import maybeMoveNodesLeftAfterDeletion from "../utils/maybeMoveNodesLeftAfterDeletion";

export function useDeleteNodes() {
  const [nodes, setNodes] = useAtom(nodesAtom);
  const [edges, setEdges] = useAtom(edgesAtom);
  const saveToBackend = useSetAtom(saveCanvasSetter);

  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const [sidebarOpenNodeId, setSidebarOpenNodeId] = useAtom(
    sidebarOpenNodeIdAtom,
  );

  const onNodesDelete = useCallback(
    async (nodesDeleted: Node[]) => {
      takeSnapshot();
      let nodesDeletedIds = nodesDeleted.map((node) => node.id);

      const zapNodeDescendantsIds: string[] = [];

      // Find path nodes and their descendants and add their IDs to nodesDeletedIds
      nodesDeleted.forEach((node) => {
        if (node.type === "zap") {
          const zapNodeId = node.id;
          const zapSteps = nodes.filter(
            (node) => node.type === "zapStep" && node.parentId === zapNodeId,
          );

          zapNodeDescendantsIds.push(
            ...zapSteps.map((descendant: Node) => descendant.id),
          );
        }
      });

      // Combine zap node descendants IDs with nodesDeletedIds and remove duplicates
      nodesDeletedIds = [
        ...new Set([...nodesDeletedIds, ...zapNodeDescendantsIds]),
      ];

      // Find orphaned annotation nodes (tour annotations) that reference deleted nodes
      const deletedIdsSet = new Set(nodesDeletedIds);
      const orphanedAnnotationIds = nodes
        .filter(
          (node) =>
            node.type === "annotation" &&
            node.parentId &&
            deletedIdsSet.has(node.parentId),
        )
        .map((node) => node.id);

      // Add orphaned annotation IDs to the deletion list
      nodesDeletedIds = [
        ...new Set([...nodesDeletedIds, ...orphanedAnnotationIds]),
      ];

      if (nodesDeletedIds.some((id) => id === sidebarOpenNodeId)) {
        setSidebarOpenNodeId("");
      }

      let createdEdges: Edge[] = [];

      const newEdges = nodesDeletedIds.reduce((acc, nodeId) => {
        const node = nodes.find((n) => n.id === nodeId);
        if (!node) return acc;

        const connectedEdges = getConnectedEdges([node], edges);

        const remainingEdges = acc.filter(
          (edge) => !connectedEdges.includes(edge),
        );

        /** This is to support multi-select deletion */
        const farthestNonDeletedIncomers = getFarthestNonDeletedIncomers(
          node,
          nodes,
          edges,
          nodesDeletedIds,
        );

        /** This is to support multi-select deletion */
        const farthestNonDeletedOutgoers = getFarthestNonDeletedOutgoers(
          node,
          nodes,
          edges,
          nodesDeletedIds,
        );

        const newCreatedEdges = farthestNonDeletedIncomers
          .flatMap(({ id: source }) =>
            farthestNonDeletedOutgoers.map(({ id: target }) => {
              const edgeAlreadyExists = createdEdges.some(
                (edge) => edge.source === source && edge.target === target,
              );

              if (!edgeAlreadyExists) {
                return {
                  id: uuid(),
                  source,
                  target,
                  sourceHandle: "right",
                  targetHandle: "left",
                  markerEnd: MARKER,
                };
              }
              return null;
            }),
          )
          .filter(Boolean) as Edge[];

        createdEdges = createdEdges.concat(newCreatedEdges);
        return remainingEdges;
      }, edges);

      let newNodes = nodes.filter((node) => !nodesDeletedIds.includes(node.id));

      // Remove parentNode from nodes if parentNode is in nodesDeletedIds
      // If we keep the parentNode reference then it can cause the entire canvas
      // to corrupt and not load
      newNodes = newNodes.map((node) => {
        if (node.parentId && nodesDeletedIds.includes(node.parentId)) {
          const parentNode = nodes.find((n) => n.id === node.parentId);
          return {
            ...node,
            parentId: undefined,
            // We add the parent x/y to the child because a child node's position is relative to the parent
            // We don't want it to jump around when the parent is deleted
            position: {
              x: node.position.x + (parentNode?.position.x || 0),
              y: node.position.y + (parentNode?.position.y || 0),
            },
          };
        }
        return node;
      });

      newNodes = maybeMoveNodesLeftAfterDeletion(
        newNodes,
        newEdges,
        createdEdges,
      );

      const updatedEdges = [...newEdges, ...createdEdges];

      const nodeIds = new Set(newNodes.map((n) => n.id));
      const edgesWithBothEndpoints = updatedEdges.filter(
        (e) => nodeIds.has(e.source) && nodeIds.has(e.target),
      );

      setEdges(edgesWithBothEndpoints);
      setNodes(newNodes);

      saveToBackend({
        nodes: newNodes,
        edges: edgesWithBothEndpoints,
      });
    },
    [
      edges,
      setEdges,
      nodes,
      saveToBackend,
      takeSnapshot,
      setNodes,
      setSidebarOpenNodeId,
      sidebarOpenNodeId,
    ],
  );
  return { onNodesDelete };
}
